/**
 * Redeem codes (spec docs/superpowers/specs/2026-10-09-redeem-codes-design.md):
 * the reward kinds a code may hold, the admin editor's box syntax, and the
 * player-facing labels. Pure — no DOM, no Supabase.
 *
 * A box reads `[xN] <kind> [name]`: `x300 coins`, `x3 bags`,
 * `x5 item goldstone`, `x2 potion chili`, `tower ninja`, `icon ninja4`.
 */
import type { TowerType } from '../core/types';
import { SHOP_ORDER } from './unlocks';
import { TOWER_NAMES } from './displayNames';
import { isGiftItem, type GiftItem } from './giftItems';
import { POTIONS, potionFromCommandName } from './potions';
import { isShopAvatarCode, SHOP_AVATARS } from './avatars';

/**
 * Shelf and currency only (the user's answer): progression gives — level,
 * rank, hublevel, unlock, achieve — would let a code skip a player's progress.
 * The SQL `redeem_rewards_valid` holds this list and these bounds too;
 * tests/unit/redeemCodes.test.ts keeps them identical.
 */
export const REWARD_KINDS = [
  'coins',
  'darkcoins',
  'lightcoins',
  'capycoins',
  'bags',
  'goldenbags',
  'capsules',
  'debris',
  'item',
  'potion',
  'tower',
  'icon',
] as const;
export type RewardKind = (typeof REWARD_KINDS)[number];

/**
 * The largest amount one box may carry: `/give`'s bounds (applyGive refuses
 * anything past them). Coins stop at a billion because the save column is a
 * 32-bit int and a balance past it makes EVERY save fail. Items and potions
 * land one at a time, so their bound is the bags'. A tower or an icon is one.
 */
export const REWARD_MAX: Record<RewardKind, number> = {
  coins: 1_000_000_000,
  darkcoins: 1_000_000,
  lightcoins: 1_000_000,
  capycoins: 1_000_000,
  bags: 1_000,
  goldenbags: 1_000,
  capsules: 1_000,
  debris: 100,
  item: 1_000,
  potion: 1_000,
  tower: 1,
  icon: 1,
};

/** Kinds whose box names a thing after the kind word. */
const NAMED: ReadonlySet<RewardKind> = new Set(['item', 'potion', 'tower', 'icon']);
/** Kinds that take no quantity — one of each exists. */
const SINGLE: ReadonlySet<RewardKind> = new Set(['tower', 'icon']);

export interface CodeReward {
  what: RewardKind;
  /** The tower / item / potion / icon name; '' for the amount kinds. */
  value: string;
  amount: number;
}

/** A code's name: 1–24 letters, digits, `-` or `_`, compared EXACTLY. */
export const CODE_NAME_MAX = 24;
export function isValidCodeName(name: string): boolean {
  return /^[A-Za-z0-9_-]{1,24}$/.test(name);
}

/** Five boxes in the editor; a code holds one to five rewards. */
export const CODE_REWARD_BOXES = 5;

export function isRewardKind(v: unknown): v is RewardKind {
  return typeof v === 'string' && (REWARD_KINDS as readonly string[]).includes(v);
}

export type RewardLineResult =
  | { ok: true; reward: CodeReward }
  | { ok: false; error: string }
  /** A blank box: not a reward, and not an error. */
  | { ok: 'empty' };

/** Parse one editor box. Kinds and names are case-insensitive; the CODE is not. */
export function parseRewardLine(line: string): RewardLineResult {
  const text = line.trim().toLowerCase().replace(/\s+/g, ' ');
  if (text === '') return { ok: 'empty' };
  const m = /^(?:x\s?(\d+) )?([a-z]+)(?: ([a-z0-9]+))?$/.exec(text);
  if (!m) return { ok: false, error: 'Incorrect' };
  const [, rawAmount, kind, name] = m;
  if (!isRewardKind(kind)) return { ok: false, error: 'Incorrect' };
  const max = REWARD_MAX[kind];
  let amount = rawAmount === undefined ? 1 : Number(rawAmount);
  if (SINGLE.has(kind)) amount = 1;
  if (!Number.isSafeInteger(amount) || amount < 1) return { ok: false, error: 'Incorrect' };
  if (amount > max)
    return { ok: false, error: `Incorrect — at most x${max.toLocaleString('en-US')}` };
  if (!NAMED.has(kind)) {
    if (name !== undefined) return { ok: false, error: 'Incorrect' };
    return { ok: true, reward: { what: kind, value: '', amount } };
  }
  if (name === undefined || !nameFits(kind, name)) return { ok: false, error: 'Incorrect' };
  // A potion is stored under its command spelling ("bottleburger").
  return { ok: true, reward: { what: kind, value: name, amount } };
}

function nameFits(kind: RewardKind, name: string): boolean {
  if (kind === 'tower') return (SHOP_ORDER as readonly string[]).includes(name);
  if (kind === 'item') return isGiftItem(name);
  if (kind === 'potion') return potionFromCommandName(name) !== null;
  if (kind === 'icon') return isShopAvatarCode(name);
  return false;
}

/** A reward back into box text — what the editor shows when a code is edited. */
export function rewardToLine(r: CodeReward): string {
  if (SINGLE.has(r.what)) return `${r.what} ${r.value}`;
  const head = `x${r.amount} ${r.what}`;
  return NAMED.has(r.what) ? `${head} ${r.value}` : head;
}

/**
 * A server payload's rewards, re-validated (an inbox row is never trusted).
 * Null when anything in it is malformed.
 */
export function parseRewards(raw: unknown): CodeReward[] | null {
  if (!Array.isArray(raw) || raw.length < 1 || raw.length > CODE_REWARD_BOXES) return null;
  const out: CodeReward[] = [];
  for (const r of raw) {
    if (typeof r !== 'object' || r === null) return null;
    const { what, value, amount } = r as Record<string, unknown>;
    if (!isRewardKind(what) || typeof value !== 'string' || typeof amount !== 'number') return null;
    const parsed = parseRewardLine(rewardToLine({ what, value, amount }));
    if (parsed.ok !== true) return null;
    out.push(parsed.reward);
  }
  return out;
}

const ITEM_NAMES: Record<GiftItem, string> = {
  diamond: 'Diamond',
  varietyorb: 'Variety Orb',
  moonstone: 'Moon Stone',
  debris: 'Space Debris',
  goldstone: 'Golden Stone',
  capsule: 'Capsule',
  goldenbag: 'Golden Takeout Bag',
  capystone: 'Capystone',
  darkshard: 'Dark Shard',
  evilorb: 'Evil Orb',
  lightshard: 'Light Shard',
  lightorb: 'Light Orb',
  darkchest: 'Dark Chest',
  lightchest: 'Light Chest',
  shroom: 'The Shroom',
  snowshroom: 'Snowshroom',
  tokenofshroom: 'Token of Shroom',
};

const AMOUNT_NAMES: Record<Exclude<RewardKind, 'item' | 'potion' | 'tower' | 'icon'>, string> = {
  coins: 'Coins',
  darkcoins: 'Dark Hambi Coins',
  lightcoins: 'Light Hambi Coins',
  capycoins: 'Capycoins',
  bags: 'Takeout Bags',
  goldenbags: 'Golden Takeout Bags',
  capsules: 'Capsules',
  debris: 'Space Debris',
};

/** What a player reads: "300 Coins", "5 × Golden Stone", "Ninja Hambi". */
export function rewardLabel(r: CodeReward): string {
  const n = r.amount.toLocaleString('en-US');
  if (r.what === 'tower') return TOWER_NAMES[r.value as TowerType] ?? r.value;
  if (r.what === 'icon')
    return `${SHOP_AVATARS.find((a) => a.code === r.value)?.label ?? r.value} icon`;
  if (r.what === 'item') return `${n} × ${ITEM_NAMES[r.value as GiftItem] ?? r.value}`;
  if (r.what === 'potion') {
    const kind = potionFromCommandName(r.value);
    return `${n} × ${kind ? POTIONS[kind].name : r.value}`;
  }
  return `${n} ${AMOUNT_NAMES[r.what]}`;
}
